import {
  PriorPolicyChallengeFormValuesType,
  PriorPolicyChallengeFieldsType,
} from 'QuoteApp/services/endpoints/PostChallengePriorPolicy';
import React, { useMemo } from 'react';
import { Column, FormInput } from 'Shared/components';
import {
  abbreviateNumber,
  validateCoverageLimitWithK,
  cleanCoverageLimitInput,
} from 'Shared/utils';
import {
  formatDateShortYearUTC,
  monthDateYearNormalizerShort,
  isServerDate,
  parseDateSlashFormatShort,
  formatDateMonthYear,
} from 'Shared/utils/date';
import styles from './styles.module.css';
import { FormikErrors, getIn } from 'formik';
import { generateFieldDataTestId } from 'QuoteApp/utils/formik';
import {
  PriorPolicyCoverageType,
  PriorPolicyResponseType,
} from 'QuoteApp/services/endpoints/GetDetails';
import { useAppContext } from 'QuoteApp/services/providers/AppProvider';
import { CoveredPartyPayloadType } from '@clearcover/core-app-types';

// Coverage limit validation functions (now using shared utilities)

// Check if at least one coverage limit is provided (mirrors Yup limits validation)
const validateAtLeastOneCoverageLimit = (values: {
  biLimit?: string | null;
  cslLimit?: string | null;
  pipLimit?: string | null;
}): boolean => {
  const { biLimit, cslLimit, pipLimit } = values;
  return !!(biLimit?.trim() || cslLimit?.trim() || pipLimit?.trim());
};

// Default empty coverage values for when data is unavailable
const EMPTY_COVERAGE_VALUES = {
  biLimit: null,
  cslLimit: null,
  pipLimit: null,
} as const;

/**
 * Extracts coverage values from form data for validation purposes.
 *
 * @param values - The form values object (either challenge form or endorsement form)
 * @param identifier - For challenge forms: the field identifier to access. For endorsement forms: ignored (use 'reports')
 * @param fromEndorsement - If true, accesses nested priorPolicies structure; if false, accesses flat challenge form structure
 * @param priorPoliciesIndex - Required when fromEndorsement is true. The index of the prior policy in the array.
 *                             If omitted for endorsement forms, a warning is logged and empty values are returned.
 *
 * @returns Object containing biLimit, cslLimit, and pipLimit values (or null if not available)
 *
 * @example
 * // For challenge forms (flat structure):
 * getCoverageValues(formValues, 'priorPolicy-123');
 *
 * // For endorsement forms (nested structure):
 * getCoverageValues(formValues, 'reports', true, 0);
 */
export const getCoverageValues = (
  values: PriorPolicyChallengeFormValuesType | CoveredPartyPayloadType,
  identifier: string,
  fromEndorsement: boolean = false,
  priorPoliciesIndex?: number
): {
  biLimit: string | null;
  cslLimit: string | null;
  pipLimit: string | null;
} => {
  if (fromEndorsement) {
    // For endorsement forms, priorPoliciesIndex should be provided
    if (priorPoliciesIndex === undefined) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          'getCoverageValues: priorPoliciesIndex should be provided when fromEndorsement is true. ' +
            'Returning empty values.'
        );
      }
      return EMPTY_COVERAGE_VALUES;
    }

    // Defensive checks: ensure the nested structure exists before accessing
    const endorsementValues = values as CoveredPartyPayloadType;
    const reports = endorsementValues.reports;
    if (!reports?.priorPolicies?.[priorPoliciesIndex]) {
      return EMPTY_COVERAGE_VALUES;
    }

    const coverages = reports.priorPolicies[priorPoliciesIndex].coverages;
    if (!coverages) {
      return EMPTY_COVERAGE_VALUES;
    }

    return {
      biLimit: coverages.bodilyInjury ?? null,
      cslLimit: coverages.combinedSingleLimit ?? null,
      pipLimit: coverages.personalInjuryProtection ?? null,
    };
  }

  // For challenge forms, access the flat structure
  const formValues = values[identifier] as PriorPolicyChallengeFieldsType;
  if (!formValues) {
    return EMPTY_COVERAGE_VALUES;
  }

  return {
    biLimit: formValues.biLimit ?? null,
    cslLimit: formValues.cslLimit ?? null,
    pipLimit: formValues.pipLimit ?? null,
  };
};

/**
 * Gets the appropriate error message for a coverage field
 * @param fromEndorsement - true for endorsement forms with nested structure (reports.priorPolicies[].coverages), false for challenge forms
 */
// Combined validation function for coverage limits (DRY implementation)
export const getCoverageErrorMessage = (
  fieldValue: string | null | undefined,
  type: 'BI' | 'CSL' | 'PIP',
  allCoverageValues: {
    biLimit?: string | null;
    cslLimit?: string | null;
    pipLimit?: string | null;
  },
  errors: FormikErrors<PriorPolicyChallengeFormValuesType>,
  identifier: string,
  fromEndorsement: boolean = false
): string | undefined => {
  const value = fieldValue?.toString() || '';

  // First check individual field validation using shared utility
  const validation = validateCoverageLimitWithK(value, type);
  if (!validation.isValid) {
    return validation.errorMessage;
  }

  // Then check if at least one coverage limit is provided
  const hasAtLeastOne = validateAtLeastOneCoverageLimit(allCoverageValues);

  // Access limits error - handle nested structure for endorsement case
  const limitsError = fromEndorsement
    ? getIn(errors, `${identifier}.coverages.limits`)
    : errors[identifier]?.limits;

  if (!hasAtLeastOne && typeof limitsError === 'string') {
    return limitsError;
  }

  // Finally return any other field-specific errors
  const fieldName =
    type === 'BI' ? 'biLimit' : type === 'CSL' ? 'cslLimit' : 'pipLimit';

  // For endorsement, get error from nested coverages path
  if (fromEndorsement) {
    const coverageFieldName =
      type === 'BI'
        ? 'bodilyInjury'
        : type === 'CSL'
        ? 'combinedSingleLimit'
        : 'personalInjuryProtection';
    return getIn(errors, `${identifier}.coverages.${coverageFieldName}`);
  }

  return errors[identifier]?.[fieldName];
};

// Coverage limit change handlers
export const handleCoverageChange = (
  fieldName: string,
  type: 'BI' | 'CSL' | 'PIP',
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
) => (e: React.ChangeEvent<HTMLInputElement>) => {
  const rawValue = e.target.value;
  const allowSlash = type === 'BI';
  const cleanedValue = cleanCoverageLimitInput(rawValue, allowSlash);

  // Store the cleaned value
  setFieldValue(fieldName, cleanedValue, false);
};

// AM Best Number change handler - only allow numeric input (max 10 digits)
const handleAmBestNumberChange = (
  fieldName: string,
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
) => (e: React.ChangeEvent<HTMLInputElement>) => {
  const rawValue = e.target.value;
  // Only allow numeric characters and limit to 10 digits (max value 9999999999)
  const numericOnly = rawValue.replace(/[^0-9]/g, '');
  const limitedValue = numericOnly.slice(0, 10);

  setFieldValue(fieldName, limitedValue, false);
};

// Date validation function for MM/DD/YY format (2-digit year)
const isValidMMDDYYDate = (dateString: string): boolean => {
  if (!dateString || dateString.length < 8) return false;

  // Check if it matches MM/DD/YY format
  const mmddyyRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{2}$/;
  if (!mmddyyRegex.test(dateString)) return false;

  // Use our consistent date parsing utility instead of date-fns with non-deterministic reference date
  try {
    const parsedDate = parseDateSlashFormatShort(dateString);
    return parsedDate !== null;
  } catch {
    return false;
  }
};

// Helper function to get the display value for a date field (DRY implementation)
const getDateDisplayValue = (dateValue: string | null | undefined): string => {
  if (!dateValue) return '';

  return isServerDate(dateValue)
    ? parseISODateForDisplay(dateValue.toString())
    : dateValue.toString();
};

// Check if a date should show validation error (complete but invalid)
const shouldShowDateError = (dateString: string): boolean => {
  if (!dateString) return false;

  // Don't show error for ISO format dates (initial values)
  if (isServerDate(dateString)) return false;

  // Only show error if the date appears complete (8 characters) but is invalid MM/DD/YY format
  if (dateString.length >= 8) {
    return !isValidMMDDYYDate(dateString);
  }

  return false;
};

// Parse ISO date string to MM/DD/YY or MM/YY format for display
export const parseISODateForDisplay = (
  isoDateString: string | undefined | null
): string => {
  if (!isoDateString) return '';

  try {
    // Check if date is in YYYY-MM format (e.g., "2026-10")
    const yearMonthPattern = /^\d{4}-\d{2}$/;
    if (yearMonthPattern.test(isoDateString)) {
      // Parse YYYY-MM format and convert to MM/YY
      const [year, month] = isoDateString.split('-');
      const twoDigitYear = year.slice(-2); // Get last 2 digits of year
      return `${month}/${twoDigitYear}`;
    }

    // Handle ISO format dates like "2026-07-30T00:00:00.000+00:00"
    return formatDateShortYearUTC(isoDateString);
  } catch (error) {
    // If parsing fails, return the original value or empty string
    return isoDateString || '';
  }
};

// Date onChange handler with formatting and validation
export const handleDateChange = (
  fieldName: string,
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
  allowFullYear: boolean = false
) => (e: React.ChangeEvent<HTMLInputElement>) => {
  const rawValue = e.target.value;
  const formattedValue = monthDateYearNormalizerShort(rawValue, allowFullYear);

  // Store the formatted value so user can see it
  setFieldValue(fieldName, formattedValue, false);
};

type PriorPolicyChallengeFormPropsType = {
  identifier: string;
  values: PriorPolicyChallengeFormValuesType;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void;
  isEditing: boolean;
  errors: FormikErrors<PriorPolicyChallengeFormValuesType>;
  priorPolicy: PriorPolicyResponseType['attributes'] | null;
  isAdding?: boolean;
};
const getInputProps = (
  fieldName: keyof PriorPolicyChallengeFieldsType,
  values: PriorPolicyChallengeFormPropsType['values'],
  identifier: string,
  setFieldValue: PriorPolicyChallengeFormPropsType['setFieldValue'],
  errors: PriorPolicyChallengeFormPropsType['errors']
) => {
  const fullFieldName = `${identifier}.${fieldName}`;
  const fieldValue = values[identifier][fieldName];
  const fieldError = errors[identifier]?.[fieldName];
  return {
    name: fullFieldName,
    className: styles.formInput,
    'data-testid': generateFieldDataTestId(fieldName),
    variant: 'selectAutocomplete' as const,
    onChange: (value: string) => {
      setFieldValue(fullFieldName, value);
    },
    value: fieldValue?.toString() || '',
    errorMessage: typeof fieldError === 'string' ? fieldError : undefined,
  };
};

const PriorPolicyChallengeForm = ({
  identifier,
  values,
  setFieldValue,
  isEditing,
  errors,
  priorPolicy,
}: PriorPolicyChallengeFormPropsType) => {
  const { config } = useAppContext();

  const carriers = useMemo(() => {
    return config?.drivers.currentInsurance.carrierName;
  }, [config]);

  const states = useMemo(() => {
    return config?.drivers.driverLicense.state;
  }, [config]);

  const renderEdit = () => {
    return (
      <>
        <Column
          variant="eighth"
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          <FormInput
            {...getInputProps(
              'carrierName',
              values,
              identifier,
              setFieldValue,
              errors
            )}
            options={Object.entries(carriers || {}).reduce(
              (acc, [_key, value]) => {
                acc[value] = value;
                return acc;
              },
              {} as Record<string, string>
            )}
          />
        </Column>
        <Column
          variant="eighth"
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          <FormInput
            {...getInputProps(
              'policyNumber',
              values,
              identifier,
              setFieldValue,
              errors
            )}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue(`${identifier}.policyNumber`, e.target.value);
            }}
            variant="text"
            placeholder="Policy #"
          />
        </Column>
        <Column
          variant="eighth"
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          <FormInput
            {...getInputProps(
              'startDate',
              values,
              identifier,
              setFieldValue,
              errors
            )}
            className={styles.formInputDate}
            variant="text"
            placeholder="MM/DD/YY"
            value={getDateDisplayValue(values[identifier].startDate)}
            onChange={handleDateChange(
              `${identifier}.startDate`,
              setFieldValue
            )}
            errorMessage={
              shouldShowDateError(
                values[identifier].startDate?.toString() || ''
              )
                ? 'Invalid format'
                : errors[identifier]?.startDate
            }
          />
        </Column>
        <Column
          variant="eighth"
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          <FormInput
            {...getInputProps(
              'endDate',
              values,
              identifier,
              setFieldValue,
              errors
            )}
            className={styles.formInputDate}
            variant="text"
            placeholder="MM/DD/YY"
            value={getDateDisplayValue(values[identifier].endDate)}
            onChange={handleDateChange(`${identifier}.endDate`, setFieldValue)}
            errorMessage={
              shouldShowDateError(values[identifier].endDate?.toString() || '')
                ? 'Invalid format'
                : errors[identifier]?.endDate
            }
          />
        </Column>
        <Column
          variant="eighth"
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          <FormInput
            {...getInputProps(
              'amBestNumber',
              values,
              identifier,
              setFieldValue,
              errors
            )}
            variant="text"
            onChange={handleAmBestNumberChange(
              `${identifier}.amBestNumber`,
              setFieldValue
            )}
          />
        </Column>
        <Column
          variant="eighth"
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          <FormInput
            {...getInputProps(
              'state',
              values,
              identifier,
              setFieldValue,
              errors
            )}
            options={Object.entries(states || {}).reduce((acc, [key]) => {
              acc[key] = key;
              return acc;
            }, {} as Record<string, string>)}
          />
        </Column>
        <Column
          variant="eighth"
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          <FormInput
            name={`${identifier}.biLimit`}
            className={styles.formInput}
            data-testid={generateFieldDataTestId('biLimit')}
            variant="text"
            placeholder="100K/300K"
            value={values[identifier].biLimit?.toString() || ''}
            onChange={handleCoverageChange(
              `${identifier}.biLimit`,
              'BI',
              setFieldValue
            )}
            errorMessage={getCoverageErrorMessage(
              values[identifier].biLimit,
              'BI',
              getCoverageValues(values, identifier),
              errors,
              identifier
            )}
          />
        </Column>
        <Column
          variant="eighth"
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          <FormInput
            name={`${identifier}.cslLimit`}
            className={styles.formInput}
            data-testid={generateFieldDataTestId('cslLimit')}
            variant="text"
            placeholder="100K"
            value={values[identifier].cslLimit?.toString() || ''}
            onChange={handleCoverageChange(
              `${identifier}.cslLimit`,
              'CSL',
              setFieldValue
            )}
            errorMessage={getCoverageErrorMessage(
              values[identifier].cslLimit,
              'CSL',
              getCoverageValues(values, identifier),
              errors,
              identifier
            )}
          />
        </Column>
        <Column
          variant="eighth"
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          <FormInput
            name={`${identifier}.pipLimit`}
            className={styles.formInput}
            data-testid={generateFieldDataTestId('pipLimit')}
            variant="text"
            placeholder="10K"
            value={values[identifier].pipLimit?.toString() || ''}
            onChange={handleCoverageChange(
              `${identifier}.pipLimit`,
              'PIP',
              setFieldValue
            )}
            errorMessage={getCoverageErrorMessage(
              values[identifier].pipLimit,
              'PIP',
              getCoverageValues(values, identifier),
              errors,
              identifier
            )}
          />
        </Column>
      </>
    );
  };

  const getCoverage = (
    coverageType: PriorPolicyCoverageType['coverageType']
  ) => {
    return (
      priorPolicy?.coverages.find(
        coverage => coverage.coverageType === coverageType
      ) || {
        coverageType,
        limitPerPerson: 0,
        limitPerAccident: 0,
      }
    );
  };

  const renderCoverageLimits = (coverage: PriorPolicyCoverageType) => {
    // For BI (bodilyInjury), show both values with slash
    if (coverage.coverageType === 'bodilyInjury') {
      return (
        <>
          <div>
            {coverage?.limitPerPerson
              ? `$${abbreviateNumber(Number(coverage.limitPerPerson))}`
              : '-'}
            /
            {coverage.limitPerAccident
              ? `$${abbreviateNumber(Number(coverage.limitPerAccident))}`
              : '-'}
          </div>
        </>
      );
    }

    // For CSL and PIP, show only the single limitPerPerson value
    return (
      <>
        <div>
          {coverage?.limitPerPerson
            ? `$${abbreviateNumber(Number(coverage.limitPerPerson))}`
            : '-'}
        </div>
      </>
    );
  };

  const renderReadOnly = () => {
    const carrierName =
      carriers?.[values[identifier].carrierName] ||
      values[identifier].carrierName;

    return (
      <>
        <Column
          variant="eighth"
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          <div className={styles.priorPolicyChallengeReadOnlyAttributeText}>
            {carrierName || '-'}
          </div>
        </Column>
        <Column
          variant="eighth"
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          <div className={styles.priorPolicyChallengeReadOnlyAttributeText}>
            {values[identifier].policyNumber || '-'}
          </div>
        </Column>
        <Column
          variant={'eighth'}
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          <span>{formatDateMonthYear(values[identifier].startDate)}</span>
        </Column>
        <Column
          variant={'eighth'}
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          <span>{formatDateMonthYear(values[identifier].endDate)}</span>
        </Column>
        <Column
          variant={'eighth'}
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          <span>{values[identifier].amBestNumber || '-'}</span>
        </Column>
        <Column
          variant={'eighth'}
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          <span>{values[identifier].state || '-'}</span>
        </Column>
        <Column
          variant={'eighth'}
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          {renderCoverageLimits(getCoverage('bodilyInjury'))}
        </Column>
        <Column
          variant={'eighth'}
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          {renderCoverageLimits(getCoverage('combinedSingleLimit'))}
        </Column>
        <Column
          variant={'eighth'}
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          {renderCoverageLimits(getCoverage('personalInjuryProtection'))}
        </Column>
      </>
    );
  };

  return (
    <div
      data-testid={`prior-policy-challenge-read-only-attributes-${identifier}`}
      className={styles.priorPolicyChallengeReadOnlyAttributesWrapper}
    >
      {isEditing ? renderEdit() : renderReadOnly()}
    </div>
  );
};

export default PriorPolicyChallengeForm;
